import {IBistasAPI, IPortalseite, ISchueler_testsAPI} from "../Interfaces";
import Utilities from "./../Utilities";
import {IDomaenenDB, ISchuelerDB} from "../InterfacesDB";
import Rueckmeldung from "./Rueckmeldung";
import * as pako from "pako";
import Statistik from "../Statistik";
import Tooltip from '../Tooltip';

interface IVERAcheckEintrag {
    schuelerNr: number;
    loesungshaeufigkeit: number | null;
    bistawert: number | null;
}

interface IVERAcheckProjektDaten {
    version: number;
    project_type: string;
    subject: string;
    domain: string | null;
    domainmodule: string;
    year: number;
    type: string;
    group_name: string;
    analyzer: string;
    feedback_key: string;
    group_results: {
        no: number;
        bista: number | null;
    }[];
}

interface IVERAcheckEnvelope {
    timestamp: string;
    version: string;
    json: IVERAcheckProjektDaten;
}

export default class RueckmeldungVERAcheck extends Rueckmeldung implements IPortalseite {

    override async onSeiteLoad(): Promise<boolean> {
        await super.onSeiteLoad();
        await this.filterAnwenden();

        document.addEventListener("refresh-rueckmeldung", () => {
            this.refreshLhSchuelerebene();
        });

        document.dispatchEvent(new Event("refresh-rueckmeldung"));

        return true;
    }

    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    override async onSeiteLeave(): Promise<boolean> {
        return true;
    }

    private portalDomaeneNachBistaDomaene(domaene: string | null): string | null {
        if (domaene === null) {
            return null;
        }

        const wert = domaene.toLowerCase();

        if (wert === "de-le") {
            return "le";
        }

        if (wert === "de-or") {
            return "or";
        }

        if (wert === "de-zu") {
            return "zu";
        }

        if (wert.includes("-")) {
            return wert.split("-")[1] ?? wert;
        }

        return wert;
    }

    private holeDomaenenObjekt(domaene: string | null): IDomaenenDB | null {
        if (domaene === null) {
            return null;
        }

        return this.domaenenliste.find((d) => d.domaene === domaene) ?? null;
    }

    private ermittleFeedbackKey(fach: string, domaene: string | null = null): string {
        if (fach === "de") {
            const bistaDomaene = this.portalDomaeneNachBistaDomaene(domaene);

            if (bistaDomaene === "le") {
                return "V8DeLe";
            }

            if (bistaDomaene === "or") {
                return "V8DeOr";
            }

            if (bistaDomaene === "zu") {
                return "V8DeZu";
            }
        }

        if (fach === "ma") {
            return "V8MDZ";
        }

        return "";
    }

    private ermittleDomainmodul(fach: string, domaene: string | null = null): string {
        if (fach === "de") {
            const bistaDomaene = this.portalDomaeneNachBistaDomaene(domaene);

            if (bistaDomaene === "le") {
                return "Lesen";
            }

            if (bistaDomaene === "or") {
                return "Orthografie";
            }

            if (bistaDomaene === "zu") {
                return "Zuhören";
            }
        }

        if (fach === "ma") {
            return "Daten und Zufall";
        }

        return this.holeDomaenenObjekt(domaene)?.bezeichnung ?? "n/a";
    }

    private findeBista(
        bistas: IBistasAPI[],
        fach: string,
        testheftId: number,
        nRichtig: number,
        domaene: string | null = null
    ): number | null {
        const portalDomaene = domaene?.toLowerCase() ?? null;
        const bistaDomaeneKurz = this.portalDomaeneNachBistaDomaene(domaene);

        const match = bistas.find((b) => {
            const bistaFach = (b.fach ?? "").toLowerCase();
            const bistaDom = (b.domaene ?? "").toLowerCase();

            const domaenePasst =
                bistaDomaeneKurz === null
                    ? true
                    : (
                        bistaDom === bistaDomaeneKurz ||
                        bistaDom === portalDomaene
                    );

            return (
                bistaFach === fach.toLowerCase() &&
                Number(b.testheft_id) === Number(testheftId) &&
                Number(b.n_richtig_geloest) === Number(nRichtig) &&
                domaenePasst
            );
        });

        return match?.bistawert != null ? Number(match.bistawert) : null;
    }

    private holeSchueler(test: ISchueler_testsAPI): ISchuelerDB | undefined {
        return this.schuelerliste.find((s) => s.schueler_id === test.schueler_id);
    }

    private holeLoesungshaeufigkeitInsgesamt(test: ISchueler_testsAPI): number | null {
        if (test.loesungshaeufigkeiten === null || test.nicht_anwesend === 1) {
            return null;
        }

        return test.loesungshaeufigkeiten.insgesamt.r ?? null;
    }

    private holeLoesungshaeufigkeitDomaene(test: ISchueler_testsAPI, domaene: string): number | null {
        if (test.loesungshaeufigkeiten === null || test.nicht_anwesend === 1) {
            return null;
        }

        const domainEintrag = test.loesungshaeufigkeiten.domaenen.find((d) => d.name === domaene);
        return domainEintrag?.werte?.r ?? null;
    }

    private baueEintrag(
        test: ISchueler_testsAPI,
        bistas: IBistasAPI[],
        loesungshaeufigkeit: number | null,
        domaene: string | null = null
    ): IVERAcheckEintrag {
        const schueler = this.holeSchueler(test);

        const bistawert = (
            loesungshaeufigkeit !== null &&
            test.fach != null &&
            test.testheft_id != null
        )
            ? this.findeBista(
                bistas,
                test.fach,
                Number(test.testheft_id),
                Number(loesungshaeufigkeit),
                domaene
            )
            : null;

        return {
            schuelerNr: schueler?.nr ?? 0,
            loesungshaeufigkeit,
            bistawert
        };
    }

    private baueVERAcheckExport(
        eintraege: IVERAcheckEintrag[],
        domaene: string | null = null
    ): IVERAcheckProjektDaten {
        const fach = this.sessionvariablen.get["fach"] ?? "";
        const feedbackKey = this.ermittleFeedbackKey(fach, domaene);
        const domainmodule = this.ermittleDomainmodul(fach, domaene);

        return {
            version: 1,
            project_type: "VERA 8",
            subject: fach === "ma" ? "Mathematik" : fach === "de" ? "Deutsch" : fach,
            domain: null,
            domainmodule,
            year: 2026,
            type: "VERA 8 - 2026",
            group_name: this.gruppeAngezeigt?.bezeichnung ?? "",
            analyzer: "bista",
            feedback_key: feedbackKey,
            group_results: eintraege
                .filter((eintrag) => eintrag.bistawert !== null)
                .map((eintrag) => ({
                    no: eintrag.schuelerNr,
                    bista: eintrag.bistawert
                }))
        };
    }

    private baueVERAcheckEnvelope(projektDaten: IVERAcheckProjektDaten): IVERAcheckEnvelope {
        const jetzt = new Date();
        const timestamp = `${jetzt.getDate()}.${jetzt.getMonth() + 1}.${jetzt.getFullYear()}`;

        return {
            timestamp,
            version: "1.0.0",
            json: projektDaten
        };
    }

    private encodeVERAcheckParam(jsonData: any): string {
        const jsonString = JSON.stringify(jsonData);
        const gzipped = pako.gzip(jsonString);

        let binary = "";
        const chunkSize = 0x8000;

        for (let i = 0; i < gzipped.length; i += chunkSize) {
            const chunk = gzipped.subarray(i, i + chunkSize);
            binary += String.fromCharCode(...chunk);
        }

        const base64 = btoa(binary);
        return encodeURIComponent(base64);
    }

    private baueVERAcheckUrl(projektDaten: IVERAcheckProjektDaten): {
        envelope: IVERAcheckEnvelope;
        param: string;
        url: string;
    } {
        const envelope = this.baueVERAcheckEnvelope(projektDaten);
        const param = this.encodeVERAcheckParam(envelope);
        const baseUrl = `https://veracheck.nibis.de/ui/feedback/${projektDaten.feedback_key}/#FD`;
        const url = baseUrl + param;

        return {
            envelope,
            param,
            url
        };
    }

    private istDebugInfosAktiv(): boolean {
        const urlParams = new URLSearchParams(window.location.search);
        const wert = (urlParams.get("debuginfos") ?? "").toLowerCase();

        return ["1", "true", "ja", "yes", "on"].includes(wert);
    }

    private renderVeracheckButton(url: string, label: string, title: string): string {
        return `
        <div>
            <a class="material veracheck-button"
               href="${url}"
               target="_blank"
               role="button"
               aria-label="${title}"
               data-tooltip="${Tooltip.html`
                   <p><strong>${title}</strong></p>
                   <p>Öffnet VERAcheck in einem neuen Fenster.</p>
               `}"
            >
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#box-arrow-up-right'/>
                </svg>
                ${label}
            </a>
        </div>
    `;
    }

    private renderDebugBlock(
        parent: HTMLElement,
        ueberschrift: string,
        loesungshaeufigkeiten: unknown,
        exportDaten: IVERAcheckProjektDaten | null,
        envelope: IVERAcheckEnvelope | null,
        param: string | null,
        url: string | null
    ): void {
        const block = document.createElement("div");
        block.className = "veracheck-debug-block";

        const h3 = document.createElement("h3");
        h3.textContent = ueberschrift;
        block.appendChild(h3);

        const h4Lh = document.createElement("h4");
        h4Lh.textContent = "Lösungshäufigkeiten / Bista-Mapping";
        block.appendChild(h4Lh);

        const preLh = document.createElement("pre");
        preLh.textContent = JSON.stringify(loesungshaeufigkeiten, null, 2);
        block.appendChild(preLh);

        const h4Projekt = document.createElement("h4");
        h4Projekt.textContent = "Projekt-Daten";
        block.appendChild(h4Projekt);

        const preProjekt = document.createElement("pre");
        preProjekt.textContent = JSON.stringify(exportDaten, null, 2);
        block.appendChild(preProjekt);

        const h4Envelope = document.createElement("h4");
        h4Envelope.textContent = "Envelope";
        block.appendChild(h4Envelope);

        const preEnvelope = document.createElement("pre");
        preEnvelope.textContent = JSON.stringify(envelope, null, 2);
        block.appendChild(preEnvelope);

        const h4Param = document.createElement("h4");
        h4Param.textContent = "Komprimierter Parameter";
        block.appendChild(h4Param);

        const textareaParam = document.createElement("textarea");
        textareaParam.style.width = "100%";
        textareaParam.style.height = "120px";
        textareaParam.value = param ?? "";
        block.appendChild(textareaParam);

        const h4Url = document.createElement("h4");
        h4Url.textContent = "VERAcheck-Link";
        block.appendChild(h4Url);

        const textareaUrl = document.createElement("textarea");
        textareaUrl.style.width = "100%";
        textareaUrl.style.height = "120px";
        textareaUrl.value = url ?? "";
        block.appendChild(textareaUrl);

        parent.appendChild(block);
    }

    private refreshLhSchuelerebene(): void {
        const testsInGruppe = Utilities.cloneObject(this.testsInGruppe) as ISchueler_testsAPI[];
        const bistas = Utilities.cloneObject(this.bistas) as IBistasAPI[];
        const container = document.querySelector(`div.rueckmeldung-veracheck`) as HTMLDivElement | null;

        if (container === null) {
            return;
        }

        if (testsInGruppe.length === 0) {
            if (this.sessionvariablen.get["fach"] && this.sessionvariablen.get["gruppeid"]) {
                container.innerHTML =
                    `Keine Schüler
                        ${((this.gruppeAngezeigt.typ === "STAMMKLASSE") ? "der Stammklasse" : "des Kurses") ?? "der Gruppe"} <i>${this.gruppeAngezeigt.bezeichnung ?? ""}</i>
                        haben Ergebnisse im Fach ${Utilities.printFachLang(this.sessionvariablen.get["fach"])} eingetragen.
                        `;
            } else {
                container.innerHTML =
                    `Wählen Sie ein Fach und eine Gruppe aus, für die Sie Rückmeldungen einsehen möchten.`;
            }
            return;
        }

        testsInGruppe.sort((a, b) => {
            const schuelerA = this.holeSchueler(a);
            const schuelerB = this.holeSchueler(b);

            return (schuelerA?.nr ?? 0) - (schuelerB?.nr ?? 0);
        });

        const fach = this.sessionvariablen.get["fach"] ?? "";
        const istDeutsch = fach === "de";
        const istMathe = fach === "ma";
        const debugInfosAktiv = this.istDebugInfosAktiv();

        const insgesamtArray: IVERAcheckEintrag[] = testsInGruppe.map((test) =>
            this.baueEintrag(
                test,
                bistas,
                this.holeLoesungshaeufigkeitInsgesamt(test),
                null
            )
        );

        const domainArrays: Record<string, IVERAcheckEintrag[]> = {};

        if (istDeutsch) {
            this.domaenenInGruppe.forEach((dom) => {
                const domainName = dom.domaene ?? null;

                if (domainName === null) {
                    return;
                }

                domainArrays[domainName] = testsInGruppe.map((test) =>
                    this.baueEintrag(
                        test,
                        bistas,
                        this.holeLoesungshaeufigkeitDomaene(test, domainName),
                        domainName
                    )
                );
            });
        }

        let buttonHtml = "";

        let veracheckExportLesen: IVERAcheckProjektDaten | null = null;
        let veracheckExportOrthografie: IVERAcheckProjektDaten | null = null;
        let veracheckExportMathe: IVERAcheckProjektDaten | null = null;

        let veracheckEnvelopeLesen: IVERAcheckEnvelope | null = null;
        let veracheckEnvelopeOrthografie: IVERAcheckEnvelope | null = null;
        let veracheckEnvelopeMathe: IVERAcheckEnvelope | null = null;

        let paramLesen: string | null = null;
        let paramOrthografie: string | null = null;
        let paramMathe: string | null = null;

        let urlLesen: string | null = null;
        let urlOrthografie: string | null = null;
        let urlMathe: string | null = null;

        if (istDeutsch) {
            Object.keys(domainArrays).forEach((domainName) => {
                const feedbackKey = this.ermittleFeedbackKey("de", domainName);

                if (feedbackKey === "V8DeLe") {
                    veracheckExportLesen = this.baueVERAcheckExport(
                        domainArrays[domainName],
                        domainName
                    );

                    const lesenData = this.baueVERAcheckUrl(veracheckExportLesen);
                    veracheckEnvelopeLesen = lesenData.envelope;
                    paramLesen = lesenData.param;
                    urlLesen = lesenData.url;

                    console.log("VERAcheck Export Lesen:", veracheckExportLesen);
                    console.log("VERAcheck Envelope Lesen:", veracheckEnvelopeLesen);
                    console.log("VERAcheck PARAM Lesen:", paramLesen);
                    console.log("VERAcheck URL Lesen:", urlLesen);
                }

                if (feedbackKey === "V8DeOr") {
                    veracheckExportOrthografie = this.baueVERAcheckExport(
                        domainArrays[domainName],
                        domainName
                    );

                    const orthografieData = this.baueVERAcheckUrl(veracheckExportOrthografie);
                    veracheckEnvelopeOrthografie = orthografieData.envelope;
                    paramOrthografie = orthografieData.param;
                    urlOrthografie = orthografieData.url;

                    console.log("VERAcheck Export Orthografie:", veracheckExportOrthografie);
                    console.log("VERAcheck Envelope Orthografie:", veracheckEnvelopeOrthografie);
                    console.log("VERAcheck PARAM Orthografie:", paramOrthografie);
                    console.log("VERAcheck URL Orthografie:", urlOrthografie);
                }
            });

            buttonHtml = `
                ${urlLesen ? this.renderVeracheckButton(
                urlLesen,
                "VERAcheck Deutsch Lesen",
                "VERAcheck Deutsch Lesen öffnen"
            ) : ""}

                ${urlOrthografie ? this.renderVeracheckButton(
                urlOrthografie,
                "VERAcheck Deutsch Orthografie",
                "VERAcheck Deutsch Orthografie öffnen"
            ) : ""}
            `;
        } else if (istMathe) {
            veracheckExportMathe = this.baueVERAcheckExport(insgesamtArray, null);
            const matheData = this.baueVERAcheckUrl(veracheckExportMathe);

            veracheckEnvelopeMathe = matheData.envelope;
            paramMathe = matheData.param;
            urlMathe = matheData.url;

            console.log("VERAcheck Export Mathe:", veracheckExportMathe);
            console.log("VERAcheck Envelope Mathe:", veracheckEnvelopeMathe);
            console.log("VERAcheck PARAM:", paramMathe);
            console.log("VERAcheck URL:", urlMathe);

            buttonHtml = this.renderVeracheckButton(
                urlMathe,
                "VERAcheck Mathematik",
                "VERAcheck Mathematik öffnen"
            );
        } else {
            container.innerHTML = `Für dieses Fach ist aktuell kein VERAcheck-Export verfügbar.`;
            return;
        }

        if (buttonHtml.trim() === "") {
            container.innerHTML = `Für die aktuelle Auswahl konnten keine VERAcheck-Links erzeugt werden.`;
            return;
        }

        container.innerHTML = `
            <div class="veracheck-buttons">
                ${buttonHtml}
            </div>
            ${debugInfosAktiv ? `<hr/><h2>Debug-Infos</h2><div class="veracheck-debug-container"></div>` : ""}
        `;

        // Statistik zur Nutzungshäufigkeit des VERACheck einbinden
        document.querySelectorAll(`.veracheck-button`)
            .forEach(b => {
                b.addEventListener('click', () => {
                    const stat = Statistik.getInstance();
                    stat.pushStat('veracheck-aufruf', `fach=${this.rmFilter.fach}&gruppeid=${this.rmFilter.gruppeId}`);
                });
            });

        if (!debugInfosAktiv) {
            return;
        }

        const debugContainer = container.querySelector(".veracheck-debug-container") as HTMLDivElement | null;
        if (debugContainer === null) {
            return;
        }

        if (istDeutsch) {
            this.renderDebugBlock(
                debugContainer,
                "Deutsch Lesen",
                domainArrays["de-le"] ?? [],
                veracheckExportLesen,
                veracheckEnvelopeLesen,
                paramLesen,
                urlLesen
            );

            this.renderDebugBlock(
                debugContainer,
                "Deutsch Orthografie",
                domainArrays["de-or"] ?? [],
                veracheckExportOrthografie,
                veracheckEnvelopeOrthografie,
                paramOrthografie,
                urlOrthografie
            );
        }

        if (istMathe) {
            this.renderDebugBlock(
                debugContainer,
                "Mathematik",
                insgesamtArray,
                veracheckExportMathe,
                veracheckEnvelopeMathe,
                paramMathe,
                urlMathe
            );
        }
    }
}
